import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Download, FileDown, Loader2 } from "lucide-react";

export default function QRCodePage() {
  const { user } = useAuth();
  const [pngUrl, setPngUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let revoke;
    (async () => {
      try {
        const r = await api.get("/qr/png", { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setPngUrl(url);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not generate QR");
      } finally {
        setLoading(false);
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, []);

  const download = async (kind) => {
    try {
      const r = await api.get(`/qr/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "pdf" ? "review-card-a6.pdf" : "review-qr.png";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="qr-page">
      <div>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-slate-900">Your QR code</h1>
        <p className="text-slate-500 mt-1">Print it, stick it, hand it out. Scans open your Google review page.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 rounded-2xl p-8 md:p-12 grid place-items-center bg-white">
          {loading ? (
            <div className="text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Generating…</div>
          ) : pngUrl ? (
            <div className="text-center" data-testid="qr-display">
              <img src={pngUrl} alt="QR" className="mx-auto w-72 h-72 rounded-2xl border-4 border-white shadow-xl" />
              <div className="mt-6 text-slate-600 text-sm">Points to: <span className="font-medium text-slate-900 break-all">{user?.google_review_link}</span></div>
            </div>
          ) : (
            <div className="text-slate-500">Add a review link in Settings first.</div>
          )}
        </Card>

        <Card className="rounded-2xl p-6 space-y-4">
          <h3 className="font-display font-bold text-lg">Downloads</h3>
          <p className="text-sm text-slate-500">Error correction level Q (25%) — resilient to scuffs and grime. Prints at exactly A6 (105×148mm) at 300 DPI.</p>
          <Button onClick={() => download("png")} className="w-full h-12 rounded-full bg-primary hover:bg-primary/90" data-testid="qr-download-png">
            <Download className="h-4 w-4 mr-2" /> Download PNG (QR only)
          </Button>
          <Button onClick={() => download("pdf")} variant="outline" className="w-full h-12 rounded-full" data-testid="qr-download-pdf">
            <FileDown className="h-4 w-4 mr-2" /> Download A6 card (PDF)
          </Button>
          <div className="text-xs text-slate-500 space-y-1 pt-2 border-t">
            <div><strong>PNG:</strong> for invoices, stickers, custom designs.</div>
            <div><strong>PDF:</strong> ready to print on standard A6 stock.</div>
          </div>
        </Card>
      </div>
    </div>
  );
}
